import Badge from "@/common/components/Badge";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { ShowHeader } from "@/common/components/ShowHeader";
import Tabs from "@/common/components/Tabs";
import { cn } from "@/common/utils/cn";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { defaultTabs } from "../../constants/establishmentCompanyTabs.constant";
import Spinner from "@/common/components/Spinner";
import { useCurrentTabInfo } from "@/common/hooks/useCurrentTabInfo";

const Show = () => {
  const { id } = useParams();

  const [isWrapped, setIsWrapped] = useState<boolean>(false);
  const listRef = useRef<HTMLDivElement>(null);
  const [tabsIsLoading, setTabIsLoading] = useState<boolean>(true);

  const {
    data: establishmentCompany,
    isPending: establishmentCompanyIsPending,
    isRefetching: establishmentCompanyIsRefetching,
    error: establishmentCompanyError = {} as any,
    refetch: refetchEstablishmentCompany,
  } = useEstablishmentCompany(id);

  const { currentTab } = useCurrentTabInfo(defaultTabs({}));

  useEffect(() => {
    const element = listRef.current;
    if (!element) return;

    const singleLineHeight = 40;

    const observer = new ResizeObserver(() => {
      if (!establishmentCompanyIsPending) {
        setIsWrapped(element.scrollHeight > singleLineHeight * 1.5);
        setTabIsLoading(false)
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, [establishmentCompanyIsPending]);

  if (!id) {
    return null;
  }

  return (
    <ErrorMessageWrapper
      className="max-h-[calc(100vh-64px)]"
      statusCode={establishmentCompanyError?.status}
      refetchFunction={refetchEstablishmentCompany}
    >
      <div>
        <ShowHeader
          isLoading={establishmentCompanyIsPending}
          goBack
          goBackProps={{
            fallback: `/establishments`,
            searchLastRouteFromReference: `/establishments/${id}`,
          }}
          title={establishmentCompany?.general_information?.fantasy_name || '--'}
          badge={(
            <>
              {establishmentCompany?.status === 'blocked' && (
                <Badge
                  Icon={React.Fragment}
                  text="Bloqueado"
                />
              )}

              {establishmentCompany?.status === 'disabled' && (
                <Badge
                  Icon={React.Fragment}
                  text="Desativado"
                />
              )}
            </>
          )}
          buttons={(
            <>
              {establishmentCompanyIsRefetching && !currentTab && (
                <div className="flex items-center gap-2">
                  <Spinner className="fill-white-800 size-4" />

                  <span className="italic text-sm">
                    Carregando...
                  </span>
                </div>
              )}
            </>
          )}
        />

        <Tabs
          tabsListClassName="flex h-auto flex-row flex-wrap justify-start w-full bg-transparent rounded-none"
          tabsListRef={listRef}
          tabsTriggerClassName={cn(
            "h-8 transition-none duration-0 rounded-none border-b border-[#444444] text-white-800 text-xs data-[state=active]:bg-transparent data-[state=active]:text-white-800 data-[state=active]:shadow-none data-[state=active]:border-blue-800",
            !isWrapped && 'flex-1'
          )}
          tabsContentClassName="mt-6"
          tabsList={defaultTabs({
            establishmentDisplayName: establishmentCompany?.general_information?.fantasy_name
          })}
          isLoading={tabsIsLoading}
        />
      </div>
    </ErrorMessageWrapper>
  );
};

export default Show;
